import React, { ReactNode, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, Grid, CircularProgress, Chip, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { selectAuth } from '../../../redux/slices/authSlice';
import { selectAdmins } from '../../../redux/slices/adminsSlice';
import { selectCities } from '../../../redux/slices/citiesSlice';
import {
  ElectricScooter,
  BatteryChargingFull,
  Speed,
  Battery20,
  TrendingUp,
  Build,
  Visibility,
  VisibilityOff,
  LocalShipping,
  BatteryAlert,
  BatteryFull
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from 'recharts';
import axios from '../../../resources/axios';
import moment from 'moment';

interface ScooterAnalyticsData {
  scooters: {
    avgBatteryPercent: string;
    statusesCounters: {
      AVAILABLE: number;
      BROKEN_IN_CITY: number;
      TRANSPORTING: number;
      LOW_BATTERY: number;
      CHARGING: number;
      MAINTENANCE: number;
      HIDDEN: number;
    };
    activeScooters: Array<{ date: string; count: number }>;
  };
  batteries: {
    totalCount: number;
    lowBattery: { [key: string]: number };
    avgMileage: number;
    avgMileageLastMonth: number;
    topMileagePerReplace: Array<{ name: string; mileage: number }>;
    avgMileagePerReplace: number;
  };
}

const ScootersAnalyticsTab = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const auth = useSelector(selectAuth);
  const adminInfo = useSelector(selectAdmins);
  const cities = useSelector(selectCities);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ScooterAnalyticsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(moment().format('YYYY-MM-DD'));

  const isSuperAdmin = useMemo(() => {
    return auth.info?.type === 'superAdmin';
  }, [auth.info?.type]);

  const currentCityId = useMemo(() => {
    return adminInfo.currentCity;
  }, [adminInfo.currentCity]);

  const handleDateFromChange = (newDateFrom: string) => {
    const daysDiff = moment(dateTo).diff(moment(newDateFrom), 'days');
    if (daysDiff > 60) {
      const adjustedDateTo = moment(newDateFrom).add(60, 'days').format('YYYY-MM-DD');
      const today = moment().format('YYYY-MM-DD');
      setDateTo(adjustedDateTo > today ? today : adjustedDateTo);
    }
    setDateFrom(newDateFrom);
  };

  const handleDateToChange = (newDateTo: string) => {
    setDateTo(newDateTo);
  };

  useEffect(() => {
    if (!isSuperAdmin && !currentCityId) return;
    
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const dateToIso = moment(dateTo).endOf('day').toISOString();
        const dateFromIso = moment(dateFrom).startOf('day').toISOString();
        
        let url = `/analytics/scooters?dateFrom=${dateFromIso}&dateTo=${dateToIso}`;
        if (!isSuperAdmin && currentCityId) {
          url += `&cityId=${currentCityId}`;
        }
        
        const response = await axios.get(url);
        setData(response.data.data);
      } catch (error) {
        const axiosError = error as any;
        console.error('Error fetching scooter analytics:', axiosError.response?.status, axiosError.message);
        if (axiosError.response?.status === 400) {
          setError(t('stats.invalidRequestFormat'));
        } else if (axiosError.response?.status === 404) {
          setError(t('stats.endpointNotFound'));
        } else {
          setError(t('stats.loadError'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isSuperAdmin, currentCityId, dateFrom, dateTo]);

  const hexToRgba = (hex: string, alpha: number = 1): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const StatCard = ({ icon, label, value, color, loading = false, suffix = '' }: { 
    icon: ReactNode, 
    label: string, 
    value: number | string, 
    color: string, 
    loading?: boolean,
    suffix?: string 
  }) => {
    const displayValue = typeof value === 'number' 
      ? value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 })
      : value;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '14px 16px',
          borderRadius: '12px',
          transition: 'all 0.2s ease',
          backgroundColor: hexToRgba(color, 0.12),
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
          minWidth: 140,
          minHeight: 80,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginBottom: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              backgroundColor: hexToRgba(color, 0.18),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& svg': {
                fontSize: 20,
                color: color
              }
            }}
          >
            {icon}
          </Box>
          {loading ? (
            <CircularProgress size={28} sx={{ color: color }} />
          ) : (
            <Typography
              sx={{
                fontSize: 26,
                fontWeight: 700,
                color: color,
                lineHeight: 1
              }}
            >
              {displayValue}{suffix}
            </Typography>
          )}
        </Box>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 600,
            color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            textAlign: 'center'
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  const statusConfig: Record<string, { label: string; color: string; icon: ReactNode }> = {
    AVAILABLE: { label: t('stats.statusAvailable'), color: '#4CAF50', icon: <ElectricScooter /> },
    BROKEN_IN_CITY: { label: t('stats.statusBrokenInCity'), color: '#F44336', icon: <Build /> },
    TRANSPORTING: { label: t('stats.statusTransporting'), color: '#FF9800', icon: <LocalShipping /> },
    LOW_BATTERY: { label: t('stats.statusLowBattery'), color: '#1a1a1a', icon: <BatteryAlert /> },
    CHARGING: { label: t('stats.statusCharging'), color: '#2196F3', icon: <BatteryChargingFull /> },
    MAINTENANCE: { label: t('stats.statusMaintenance'), color: '#9C27B0', icon: <Build /> },
    HIDDEN: { label: t('stats.statusHidden'), color: '#607D8B', icon: <VisibilityOff /> }
  };

  const chartData = useMemo(() => {
    if (!data?.scooters.activeScooters) return [];
    return data.scooters.activeScooters.map(item => ({
      date: moment(item.date).format('DD.MM'),
      count: item.count
    }));
  }, [data]);

  const topBatteriesData = useMemo(() => {
    if (!data?.batteries.topMileagePerReplace) return [];
    return data.batteries.topMileagePerReplace.slice(0, 5);
  }, [data]);

  if (!isSuperAdmin && !currentCityId) {
    return (
      <Paper sx={{ padding: '20px', borderRadius: 2 }}>
        <Typography color="text.secondary">
          {t('stats.selectCityPrompt')}
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ padding: '20px', borderRadius: 2 }}>
        <Typography color="error">
          {error}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Paper
        sx={{
          padding: '20px',
          border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              fontSize: 18,
              color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF'
            }}
          >
            {t('stats.scooterStatuses')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              type="date"
              label={t('stats.dateFrom')}
              value={dateFrom}
              onChange={(e) => handleDateFromChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: 150 }}
            />
            <TextField
              type="date"
              label={t('stats.dateTo')}
              value={dateTo}
              onChange={(e) => handleDateToChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: 150 }}
            />
          </Box>
        </Box>

        <Grid container spacing={2} justifyContent="center">
          {Object.entries(statusConfig).map(([status, config]) => (
            <Grid item xs={6} sm={4} md={3} lg={3} key={status}>
              <StatCard
                icon={config.icon}
                label={config.label}
                value={data?.scooters.statusesCounters[status as keyof typeof data.scooters.statusesCounters] ?? 0}
                color={config.color}
                loading={loading}
              />
            </Grid>
          ))}
          <Grid item xs={6} sm={4} md={3} lg={3}>
            <StatCard
              icon={<BatteryFull />}
              label={t('stats.avgBattery')}
              value={parseFloat(data?.scooters.avgBatteryPercent ?? '0')}
              color="#00BCD4"
              loading={loading}
              suffix="%"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper
        sx={{
          padding: '20px',
          border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
          borderRadius: 2
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            fontSize: 18,
            color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
            mb: 3
          }}
        >
          {t('stats.activeScootersPerDay')}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : chartData && chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'} />
              <XAxis 
                dataKey="date" 
                stroke={theme.palette.mode === 'light' ? '#757575' : '#9E9E9E'}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                stroke={theme.palette.mode === 'light' ? '#757575' : '#9E9E9E'}
                tick={{ fontSize: 12 }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#424242',
                  border: 'none',
                  borderRadius: 8,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
                labelStyle={{ color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF' }}
              />
              <Line 
                type="monotone" 
                dataKey="count" 
                stroke="#5073F8" 
                strokeWidth={2}
                dot={{ fill: '#5073F8', strokeWidth: 2 }}
                activeDot={{ r: 6, fill: '#5073F8' }}
                name={t('stats.activeScootersName')}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {t('stats.noDataForPeriod')}
            </Typography>
          </Box>
        )}
      </Paper>

      <Paper
        sx={{
          padding: '20px',
          border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
          borderRadius: 2
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            fontSize: 18,
            color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
            mb: 3
          }}
        >
          {t('stats.batteryAnalytics')}
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={3}>
            <StatCard
              icon={<BatteryChargingFull />}
              label={t('stats.totalBatteries')}
              value={data?.batteries.totalCount ?? 0}
              color="#4CAF50"
              loading={loading}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <StatCard
              icon={<Battery20 />}
              label={t('stats.batteryBelow20')}
              value={data?.batteries.lowBattery?.['20'] ?? 0}
              color="#F44336"
              loading={loading}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <StatCard
              icon={<Speed />}
              label={t('stats.avgMileage')}
              value={Math.round(data?.batteries.avgMileage ?? 0)}
              color="#2196F3"
              loading={loading}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <StatCard
              icon={<TrendingUp />}
              label={t('stats.avgMileagePerReplacement')}
              value={Math.round(data?.batteries.avgMileagePerReplace ?? 0)}
              color="#FF9800"
              loading={loading}
              suffix=" км"
            />
          </Grid>
        </Grid>

        {topBatteriesData.length > 0 && (
          <Box>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                mb: 2
              }}
            >
              {t('stats.topBatteriesByMileage')}
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={topBatteriesData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'} />
                <XAxis 
                  type="number" 
                  stroke={theme.palette.mode === 'light' ? '#757575' : '#9E9E9E'}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  stroke={theme.palette.mode === 'light' ? '#757575' : '#9E9E9E'}
                  tick={{ fontSize: 12 }}
                  width={100}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#424242',
                    border: 'none',
                    borderRadius: 8
                  }}
                  formatter={(value: number) => [`${value} km`, t('stats.mileage')]}
                />
                <Bar dataKey="mileage" fill="#5073F8" radius={[0, 4, 4, 0]}>
                  {topBatteriesData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={['#5073F8', '#7C4DFF', '#00BCD4', '#4CAF50', '#FF9800'][index % 5]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ScootersAnalyticsTab;
